import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
    Clock, 
    LogIn, 
    LogOut, 
    Calendar,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    User,
    Timer,
    RefreshCw,
    AlertCircle
} from 'lucide-react';
import MotivationModule from '../components/MotivationModule';

const ClockPage = () => {
    const { api, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [todayData, setTodayData] = useState(null);
    const [clockingIn, setClockingIn] = useState(false);
    const [clockingOut, setClockingOut] = useState(false);
    const [lateAlert, setLateAlert] = useState(null);
    const [noStaffProfile, setNoStaffProfile] = useState(false);

    // Check if user is Directeur (doesn't need staff profile to view)
    const isDirecteur = user?.role === 'DIRECTEUR';

    // Gabon timezone (Africa/Libreville - UTC+1)
    const GABON_TIMEZONE = 'Africa/Libreville';

    // Get current time in Gabon timezone
    const getGabonTime = () => {
        return new Date().toLocaleString('fr-FR', { timeZone: GABON_TIMEZONE });
    };

    // Update current time every second (Gabon time)
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Format time for Gabon timezone
    const formatGabonTime = (format) => {
        return currentTime.toLocaleString('fr-FR', { 
            timeZone: GABON_TIMEZONE,
            ...format 
        });
    };

    const fetchTodayData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api().get('/schedules/my-today');
            setTodayData(response.data);
            // Check if user has no staff profile linked
            if (!response.data.staff && !isDirecteur) {
                setNoStaffProfile(true);
            } else {
                setNoStaffProfile(false);
            }
        } catch (error) {
            console.error('Error fetching today data:', error);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchTodayData();
        // Poll every 15 seconds for real-time updates
        const interval = setInterval(fetchTodayData, 15000);
        return () => clearInterval(interval);
    }, [fetchTodayData]);

    const handleClockIn = async () => {
        setClockingIn(true);
        try {
            const response = await api().post('/attendance/clock-in', {
                device_type: /mobile|android|iphone/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
            });
            
            toast.success(response.data.message);
            
            // Show late alert WITHOUT penalty amount (confidentiality)
            if (response.data.status === 'LATE') {
                setLateAlert({
                    minutes: response.data.late_minutes,
                    lateCountThisMonth: response.data.late_count_this_month || 0,
                    absenceCountThisMonth: response.data.absence_count_this_month || 0
                });
            }
            
            fetchTodayData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur de pointage');
        } finally {
            setClockingIn(false);
        }
    };

    const handleClockOut = async () => {
        setClockingOut(true);
        try {
            const response = await api().post('/attendance/clock-out', {
                device_type: /mobile|android|iphone/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
            });
            
            toast.success(`Depart enregistre - ${response.data.total_work_hours}h travaillees`);
            fetchTodayData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur de pointage');
        } finally {
            setClockingOut(false);
        }
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '--:--';
        const date = new Date(dateStr);
        return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    const getStatusInfo = (status) => {
        const configs = {
            'PENDING': { icon: Clock, color: 'text-gray-500', bg: 'bg-gray-100', label: 'En attente' },
            'PRESENT': { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-100', label: 'Présent' },
            'LATE': { icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Retard' },
            'ABSENT': { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Absent' },
            'AUTO_ABSENT': { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', label: 'Absent (auto)' },
            'NOT_CLOCKED': { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-100', label: 'Non pointé' }
        };
        return configs[status] || configs['PENDING'];
    };

    // Déterminer l'état du bouton de pointage (BLEU/VERT/ROUGE)
    const getClockButtonState = () => {
        const isClosed = todayData?.attendance?.is_closed;
        
        // ROUGE : Shift clôturé ou absent
        if (isClosed) {
            return { 
                canClockIn: false, 
                canClockOut: false, 
                state: 'closed',
                inBtnClass: 'bg-red-100 text-red-400 cursor-not-allowed',
                outBtnClass: 'bg-red-100 text-red-400 cursor-not-allowed',
                inLabel: 'Shift clôturé',
                outLabel: 'Shift clôturé'
            };
        }
        
        if (attendanceStatus === 'ABSENT' || attendanceStatus === 'AUTO_ABSENT') {
            return {
                canClockIn: false,
                canClockOut: false,
                state: 'absent',
                inBtnClass: 'bg-red-100 text-red-400 cursor-not-allowed',
                outBtnClass: 'bg-red-100 text-red-400 cursor-not-allowed',
                inLabel: 'Absent',
                outLabel: 'Absent'
            };
        }
        
        // VERT : Déjà pointé entrée
        if (hasCheckedIn && !hasCheckedOut) {
            return {
                canClockIn: false,
                canClockOut: true,
                state: 'checked_in',
                inBtnClass: 'bg-green-500 text-white cursor-not-allowed',
                outBtnClass: 'bg-blue-600 hover:bg-blue-700 text-white',
                inLabel: `Arrivé ${formatTime(todayData.attendance?.check_in_time)}`,
                outLabel: 'Je termine'
            };
        }
        
        // VERT complet : Pointé entrée et sortie
        if (hasCheckedIn && hasCheckedOut) {
            return {
                canClockIn: false,
                canClockOut: false,
                state: 'completed',
                inBtnClass: 'bg-green-500 text-white cursor-not-allowed',
                outBtnClass: 'bg-green-500 text-white cursor-not-allowed',
                inLabel: `Arrivé ${formatTime(todayData.attendance?.check_in_time)}`,
                outLabel: `Parti ${formatTime(todayData.attendance?.check_out_time)}`
            };
        }
        
        // BLEU : Peut pointer
        return {
            canClockIn: true,
            canClockOut: false,
            state: 'ready',
            inBtnClass: 'bg-blue-600 hover:bg-blue-700 text-white',
            outBtnClass: 'bg-gray-200 text-gray-400 cursor-not-allowed',
            inLabel: 'Je commence',
            outLabel: 'Je termine'
        };
    };

    const hasSchedule = todayData?.schedule;
    const hasCheckedIn = todayData?.attendance?.check_in_time;
    const hasCheckedOut = todayData?.attendance?.check_out_time;
    const attendanceStatus = todayData?.attendance?.status || 'PENDING';
    const statusInfo = getStatusInfo(attendanceStatus);
    const StatusIcon = statusInfo.icon;

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
                <Card className="w-full max-w-md animate-pulse">
                    <CardContent className="p-8">
                        <div className="h-32 bg-muted rounded-xl mb-4"></div>
                        <div className="h-16 bg-muted rounded-xl"></div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Show message if user has no staff profile linked (except for Directeur)
    if (noStaffProfile) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-background to-muted/50 p-4" data-testid="clock-page">
                <div className="max-w-md mx-auto space-y-6">
                    <Card className="bg-accent text-white overflow-hidden">
                        <CardContent className="p-6 text-center">
                            <div className="text-5xl font-bold font-mono">
                                {formatGabonTime({ hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </div>
                            <div className="text-white/80 mt-2">
                                {formatGabonTime({ weekday: 'long', day: 'numeric', month: 'long' })}
                            </div>
                            <div className="text-white/60 text-xs mt-1">
                                Heure de Libreville (Gabon)
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-orange-200 bg-orange-50">
                        <CardContent className="p-6">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                    <AlertCircle className="w-6 h-6 text-orange-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-orange-900">Profil employé requis</h3>
                                    <p className="text-sm text-orange-700 mt-1">
                                        Votre compte utilisateur n'est pas lié à un profil employé. 
                                        Contactez votre directeur pour qu'il associe votre compte à votre fiche employé.
                                    </p>
                                    <p className="text-xs text-orange-600 mt-3">
                                        Connecté en tant que : <strong>{user?.name}</strong> ({user?.role})
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-background to-muted/50 p-4" data-testid="clock-page">
            <div className="max-w-2xl mx-auto space-y-6">
                {/* Motivation Module - Objectif du jour + Employé du mois */}
                <MotivationModule />
                
                {/* Header with Time */}
                <Card className="bg-accent text-white overflow-hidden max-w-md mx-auto">
                    <CardContent className="p-6 text-center relative">
                        <div className="absolute top-4 right-4">
                            <Button 
                                variant="ghost" 
                                size="icon" 
                                className="text-white/70 hover:text-white hover:bg-white/10"
                                onClick={fetchTodayData}
                                data-testid="refresh-btn"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                        </div>
                        
                        <div className="text-5xl font-bold font-mono" data-testid="current-time">
                            {formatGabonTime({ hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </div>
                        <div className="text-white/80 mt-2">
                            {formatGabonTime({ weekday: 'long', day: 'numeric', month: 'long' })}
                        </div>
                        <div className="text-white/60 text-xs mt-1">
                            Heure de Libreville (Gabon)
                        </div>
                    </CardContent>
                </Card>

                {/* User Info */}
                <Card className="max-w-md mx-auto">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                            <User className="w-6 h-6 text-accent" />
                        </div>
                        <div>
                            <div className="font-semibold" data-testid="staff-name">{todayData?.staff?.full_name || user?.name}</div>
                            <div className="text-sm text-muted-foreground">
                                {todayData?.staff?.position} - {todayData?.staff?.service}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Today's Schedule */}
                {hasSchedule ? (
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-accent" />
                                Shift prevu aujourd'hui
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-center gap-4 py-4">
                                <div className="text-center">
                                    <div className="text-2xl font-bold font-mono" data-testid="shift-start">{todayData.schedule.start_time}</div>
                                    <div className="text-xs text-muted-foreground">Debut</div>
                                </div>
                                <div className="text-2xl text-muted-foreground">→</div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold font-mono" data-testid="shift-end">{todayData.schedule.end_time}</div>
                                    <div className="text-xs text-muted-foreground">Fin</div>
                                </div>
                            </div>
                            
                            {/* Status */}
                            <div className={`p-4 rounded-xl ${statusInfo.bg} flex items-center justify-center gap-2`} data-testid="attendance-status">
                                <StatusIcon className={`w-5 h-5 ${statusInfo.color}`} />
                                <span className={`font-semibold ${statusInfo.color}`}>{statusInfo.label}</span>
                            </div>
                            
                            {/* Check-in/out times */}
                            {(hasCheckedIn || hasCheckedOut) && (
                                <div className="grid grid-cols-2 gap-4 pt-2">
                                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                                        <div className="text-sm text-muted-foreground mb-1">Arrivee</div>
                                        <div className="font-mono font-bold" data-testid="check-in-time">
                                            {formatTime(todayData.attendance?.check_in_time)}
                                        </div>
                                        {todayData.attendance?.late_minutes > 0 && (
                                            <Badge variant="outline" className="mt-1 text-xs bg-yellow-50 text-yellow-700">
                                                +{todayData.attendance.late_minutes} min
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                                        <div className="text-sm text-muted-foreground mb-1">Depart</div>
                                        <div className="font-mono font-bold" data-testid="check-out-time">
                                            {formatTime(todayData.attendance?.check_out_time)}
                                        </div>
                                        {todayData.attendance?.total_work_minutes > 0 && (
                                            <div className="text-xs text-muted-foreground mt-1">
                                                {(todayData.attendance.total_work_minutes / 60).toFixed(1)}h
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <Card>
                        <CardContent className="p-8 text-center">
                            <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                            <p className="text-muted-foreground" data-testid="no-shift-message">
                                {todayData?.message || "Pas de shift prevu aujourd'hui"}
                            </p>
                        </CardContent>
                    </Card>
                )}

                {/* Clock Buttons */}
                {hasSchedule && (
                    <div className="space-y-4">
                        {/* État du pointage */}
                        {(() => {
                            const btnState = getClockButtonState();
                            return (
                                <>
                                    {/* Indicateur d'état */}
                                    <div className={`text-center py-2 px-4 rounded-lg ${
                                        btnState.state === 'ready' ? 'bg-blue-50 text-blue-700' :
                                        btnState.state === 'checked_in' ? 'bg-green-50 text-green-700' :
                                        btnState.state === 'completed' ? 'bg-green-50 text-green-700' :
                                        'bg-red-50 text-red-700'
                                    }`}>
                                        <span className="text-sm font-medium">
                                            {btnState.state === 'ready' && '🔵 Prêt à pointer'}
                                            {btnState.state === 'checked_in' && '🟢 En poste'}
                                            {btnState.state === 'completed' && '✅ Journée terminée'}
                                            {btnState.state === 'closed' && '🔒 Shift clôturé'}
                                            {btnState.state === 'absent' && '❌ Absence enregistrée'}
                                        </span>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <Button 
                                            size="lg"
                                            className={`h-20 text-lg transition-all ${btnState.inBtnClass}`}
                                            onClick={handleClockIn}
                                            disabled={!btnState.canClockIn || clockingIn}
                                            data-testid="clock-in-btn"
                                        >
                                            <LogIn className="w-6 h-6 mr-2" />
                                            {clockingIn ? 'Pointage...' : btnState.inLabel}
                                        </Button>
                                        
                                        <Button 
                                            size="lg"
                                            className={`h-20 text-lg transition-all ${btnState.outBtnClass}`}
                                            onClick={handleClockOut}
                                            disabled={!btnState.canClockOut || clockingOut}
                                            data-testid="clock-out-btn"
                                        >
                                            <LogOut className="w-6 h-6 mr-2" />
                                            {clockingOut ? 'Pointage...' : btnState.outLabel}
                                        </Button>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                )}

                {/* Instructions */}
                <Card className="bg-muted/30">
                    <CardContent className="p-4 text-center text-sm text-muted-foreground">
                        <Timer className="w-5 h-5 mx-auto mb-2 text-accent" />
                        Pointez a l'arrivee et au depart de chaque shift.
                        <br />
                        Tolerance de retard : 10 minutes.
                    </CardContent>
                </Card>
            </div>

            {/* Late Alert Dialog - NO PENALTY AMOUNT (confidentiality) */}
            <AlertDialog open={!!lateAlert} onOpenChange={() => setLateAlert(null)}>
                <AlertDialogContent data-testid="late-alert-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
                            <AlertTriangle className="w-5 h-5" />
                            Retard enregistre
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-base space-y-4">
                            <div className="p-4 bg-orange-50 rounded-lg text-center border border-orange-200">
                                <p className="text-sm text-orange-600 mb-1">Retard</p>
                                <p className="text-3xl font-bold text-orange-700">
                                    {lateAlert?.minutes} minutes
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 bg-yellow-50 rounded-lg text-center border border-yellow-200">
                                    <p className="text-xs text-yellow-600 mb-1">Retards ce mois-ci</p>
                                    <p className="text-xl font-bold text-yellow-700">
                                        {lateAlert?.lateCountThisMonth} jour{lateAlert?.lateCountThisMonth > 1 ? 's' : ''}
                                    </p>
                                </div>
                                <div className="p-3 bg-red-50 rounded-lg text-center border border-red-200">
                                    <p className="text-xs text-red-600 mb-1">Absences ce mois-ci</p>
                                    <p className="text-xl font-bold text-red-700">
                                        {lateAlert?.absenceCountThisMonth} jour{lateAlert?.absenceCountThisMonth > 1 ? 's' : ''}
                                    </p>
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction onClick={() => setLateAlert(null)} data-testid="late-alert-confirm">
                            J'ai compris
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default ClockPage;
